/**
 * Cleaning preview card — actionable card for cleaning results.
 *
 * Uses ResultCard (variant="cleaning") to display:
 *   - Per-operation summary breakdown
 *   - Warnings from the cleaning service
 *   - Before/after comparison table (truncated for large data)
 *   - Partial preview notice when data is truncated
 *   - ActionPreviewPanel for write confirmation (when onApply is provided)
 */

import React, { useState } from 'react';
import {
  Badge,
  Text,
  Radio,
  RadioGroup,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { BroomRegular, ArrowDownloadRegular } from '@fluentui/react-icons';
import type { CleaningOperationSummary } from '@excel-ai-studio/shared';
import { CLEANING_OPERATIONS_MAP } from '@excel-ai-studio/shared';
import { ResultCard } from '../common/ResultCard';
import { ActionPreviewPanel } from '../common/ActionPreviewPanel';

/** Maximum rows displayed in the preview table */
const PREVIEW_MAX_ROWS = 50;

type WriteStrategy = 'in_place' | 'adjacent';

interface CleaningPreviewCardProps {
  readonly originalValues: unknown[][];
  readonly cleanedValues: unknown[][];
  readonly operationSummaries: readonly CleaningOperationSummary[];
  readonly changesCount: number;
  readonly warnings: readonly string[];
  /** Selection address (e.g. "Feuil1!A1:C10"). */
  readonly selectionAddress?: string;
  /** Callback to apply the cleaning. Receives cleaned values and write strategy. */
  readonly onApply?: (cleanedValues: unknown[][], strategy: WriteStrategy) => Promise<void>;
  /** Callback when user cancels / dismisses. */
  readonly onCancel?: () => void;
}

export const CleaningPreviewCard: React.FC<CleaningPreviewCardProps> = ({
  originalValues,
  cleanedValues,
  operationSummaries,
  changesCount,
  warnings,
  selectionAddress,
  onApply,
  onCancel,
}) => {
  const [writeStrategy, setWriteStrategy] = useState<WriteStrategy>('adjacent');
  const originalTruncated = originalValues.slice(0, PREVIEW_MAX_ROWS);
  const cleanedTruncated = cleanedValues.slice(0, PREVIEW_MAX_ROWS);
  const totalRows = originalValues.length;
  const rowsOmitted = totalRows - originalTruncated.length;
  const removedRows = originalValues.length - cleanedValues.length;
  const colCount = Math.max(
    originalValues[0]?.length ?? 0,
    cleanedValues[0]?.length ?? 0,
  );

  const metadataBadges = (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      <Badge
        appearance="filled"
        color={changesCount > 0 ? 'success' : 'informative'}
        size="small"
      >
        {changesCount} modification{changesCount !== 1 ? 's' : ''}
      </Badge>
      {removedRows > 0 && (
        <Badge appearance="filled" color="warning" size="small">
          {removedRows} ligne{removedRows !== 1 ? 's' : ''} supprimee{removedRows !== 1 ? 's' : ''}
        </Badge>
      )}
    </div>
  );

  return (
    <ResultCard
      title="Resultat du nettoyage"
      icon={<BroomRegular />}
      variant="cleaning"
      metadata={metadataBadges}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* ── Per-operation summary table ── */}
        {operationSummaries.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={summaryThStyle}>Operation</th>
                <th style={summaryThStyle}>Modifiees</th>
                <th style={summaryThStyle}>Ignorees</th>
                <th style={summaryThStyle}>Lignes supp.</th>
              </tr>
            </thead>
            <tbody>
              {operationSummaries.map((s) => {
                const meta = CLEANING_OPERATIONS_MAP[s.operation];
                const hasEffect = s.cellsChanged > 0 || s.rowsRemoved > 0;
                return (
                  <tr key={s.operation} style={{ opacity: hasEffect ? 1 : 0.6 }}>
                    <td style={summaryTdStyle}>{meta?.label ?? s.operation}</td>
                    <td style={summaryTdNumStyle}>{s.cellsChanged || '\u2014'}</td>
                    <td style={summaryTdNumStyle}>{s.cellsSkipped || '\u2014'}</td>
                    <td style={summaryTdNumStyle}>{s.rowsRemoved || '\u2014'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* ── No changes info ── */}
        {changesCount === 0 && (
          <MessageBar intent="info" style={{ borderRadius: 4 }}>
            <MessageBarBody>Aucune modification detectee. Les donnees sont deja propres.</MessageBarBody>
          </MessageBar>
        )}

        {/* ── Warnings from service ── */}
        {warnings.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {warnings.map((w, i) => (
              <MessageBar key={i} intent="warning" style={{ borderRadius: 4 }}>
                <MessageBarBody>{w}</MessageBarBody>
              </MessageBar>
            ))}
          </div>
        )}

        {/* ── Partial preview notice ── */}
        {rowsOmitted > 0 && (
          <MessageBar intent="info" style={{ borderRadius: 4 }}>
            <MessageBarBody>
              Apercu partiel : {PREVIEW_MAX_ROWS} lignes sur {totalRows} affichees.
              {' '}{rowsOmitted} ligne{rowsOmitted > 1 ? 's' : ''} masquee{rowsOmitted > 1 ? 's' : ''}.
              Le nettoyage s&apos;appliquera a l&apos;ensemble des donnees.
            </MessageBarBody>
          </MessageBar>
        )}

        {/* ── Before/After table ── */}
        {changesCount > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={thStyle}>#</th>
                  {Array.from({ length: colCount }, (_, i) => (
                    <th key={`orig-${i}`} style={thStyle}>Avant</th>
                  ))}
                  {Array.from({ length: colCount }, (_, i) => (
                    <th key={`clean-${i}`} style={{ ...thStyle, color: 'var(--success)' }}>Apres</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {originalTruncated.map((origRow, rowIdx) => {
                  const cleanRow = cleanedTruncated[rowIdx];
                  const rowChanged = !cleanRow || origRow.some(
                    (cell, colIdx) => String(cell ?? '') !== String(cleanRow[colIdx] ?? ''),
                  );
                  // For large datasets, only show rows that changed
                  if (!rowChanged && totalRows > 20) return null;
                  return (
                    <tr key={rowIdx}>
                      <td style={tdRowNumStyle}>{rowIdx + 1}</td>
                      {origRow.map((cell, colIdx) => {
                        const origStr = String(cell ?? '');
                        const cleanStr = cleanRow ? String(cleanRow[colIdx] ?? '') : '';
                        const changed = origStr !== cleanStr;
                        return (
                          <td key={`o-${colIdx}`} style={{ ...tdStyle, ...(changed ? changedOrigStyle : {}) }}>
                            {origStr || '\u00A0'}
                          </td>
                        );
                      })}
                      {cleanRow ? (
                        cleanRow.map((cell, colIdx) => {
                          const origStr = String(origRow[colIdx] ?? '');
                          const cleanStr = String(cell ?? '');
                          const changed = origStr !== cleanStr;
                          return (
                            <td key={`c-${colIdx}`} style={{ ...tdStyle, ...(changed ? changedCleanStyle : {}) }}>
                              {cleanStr || '\u00A0'}
                            </td>
                          );
                        })
                      ) : (
                        Array.from({ length: colCount }, (_, i) => (
                          <td key={`c-${i}`} style={{ ...tdStyle, ...removedStyle }}>
                            (supprime)
                          </td>
                        ))
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Apply confirmation panel ── */}
      {onApply && changesCount > 0 && (
        <ActionPreviewPanel
          impact={{
            target: selectionAddress ?? 'selection active',
            description:
              writeStrategy === 'adjacent'
                ? 'Les donnees nettoyees seront ecrites dans les colonnes immediatement a droite de la selection.'
                : 'Les donnees nettoyees ecraseront les valeurs originales dans la selection.',
            cellsAffected: changesCount,
          }}
          warnings={
            writeStrategy === 'in_place'
              ? ['Attention : l\'ecriture sur place ecrasera definitivement les donnees originales. Utilisez Ctrl+Z immediatement apres pour annuler si necessaire.']
              : undefined
          }
          onConfirm={() => onApply(cleanedValues, writeStrategy)}
          onCancel={onCancel ?? (() => {})}
          confirmLabel="Appliquer le nettoyage"
          confirmIcon={<ArrowDownloadRegular />}
        >
          <div style={{ marginBottom: 4 }}>
            <Text size={200} weight="semibold" style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
              Strategie d&apos;ecriture
            </Text>
            <RadioGroup
              value={writeStrategy}
              onChange={(_e, data) => setWriteStrategy(data.value as WriteStrategy)}
            >
              <Radio
                value="adjacent"
                label="Colonne adjacente (conserve les originaux)"
              />
              <Radio
                value="in_place"
                label="Sur place (ecrase les originaux)"
              />
            </RadioGroup>
          </div>
        </ActionPreviewPanel>
      )}
    </ResultCard>
  );
};

// ─── Summary table styles ────────────────────────────────

const summaryThStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '4px 8px',
  borderBottom: '1px solid var(--border-color)',
  fontWeight: 600,
  fontSize: 11,
  color: 'var(--text-secondary)',
};

const summaryTdStyle: React.CSSProperties = {
  padding: '3px 8px',
  borderBottom: '1px solid var(--border-color)',
  fontSize: 12,
};

const summaryTdNumStyle: React.CSSProperties = {
  ...summaryTdStyle,
  textAlign: 'center',
  fontFamily: 'monospace',
  color: 'var(--text-secondary)',
};

// ─── Before/After table styles ───────────────────────────

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '4px 6px',
  borderBottom: '1px solid var(--border-color)',
  fontWeight: 600,
  fontSize: 11,
  color: 'var(--text-secondary)',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '3px 6px',
  borderBottom: '1px solid var(--border-color)',
  verticalAlign: 'top',
  maxWidth: 160,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const tdRowNumStyle: React.CSSProperties = {
  ...tdStyle,
  color: 'var(--text-secondary)',
  fontWeight: 600,
  fontSize: 11,
  width: 30,
};

const changedOrigStyle: React.CSSProperties = {
  backgroundColor: 'rgba(209, 52, 56, 0.08)',
  textDecoration: 'line-through',
  color: 'var(--text-secondary)',
};

const changedCleanStyle: React.CSSProperties = {
  backgroundColor: 'rgba(16, 124, 16, 0.08)',
  fontWeight: 600,
};

const removedStyle: React.CSSProperties = {
  backgroundColor: 'rgba(209, 52, 56, 0.08)',
  color: 'var(--error)',
  fontStyle: 'italic',
};
